import { Dictionary, isEmpty, keyBy, memoize } from "lodash";
import NotAuthorized from "components/base/Errors/NotAuthorized";
import { IRouteConfig } from "services/typings";
import {
  Permission,
  Resource,
  UserType,
} from "components/CurrentUser/constants";
import { lazy } from "utils/route";
import React from "react";
import PageLoading from "components/base/PageLoading";
import Welcome from "components/base/Welcome";
import Root from "components/Root";
import { isMicroApp } from "utils/microApp";

const getRoutes = (): IRouteConfig[] => [
  {
    component: Root,
    suspense: false,
    routes: [
      ...require("./pages/Auth/routes").default,
      ...require("./pages/Seller/routes").default,
      {
        path: "/",
        component: lazy(() => import("layouts/PrivateLayout")),
        suspense: true,
        placeholder: () => <PageLoading loading={true} />,
        routes: [
          {
            path: "/",
            component: Welcome,
            suspense: false,
            exact: true,
          },
          ...require("./pages/DashBoard/routes").default,
          ...require("./pages/UniversalCampaign/routes").default,
          ...require("./pages/PartnerCampaign/routes").default,
          ...require("./pages/Report/routes").default,
          ...require("./pages/Advertiser/routes").default,
          ...require("./pages/GetLink/routes").default,
          ...require("./pages/Coupon/routes").default,
          ...require("./pages/Crosscheck/routes").default,
          ...require("./pages/Transaction/routes").default,
          ...require("./pages/Capacity/routes").default,
          ...require("./pages/BaseCommission/routes").default,
          ...require("./pages/Contract/routes").default,
          ...require("./pages/PublisherPartner/routes").default,
          ...require("./pages/Utilities/routes").default,
          ...require("./pages/Account/routes").default,
          ...require("./pages/SystemConfig/routes").default,
          ...require("./pages/BrandSolutionPortal/routes").default,
          {
            path: "*",
            component: lazy(() => import("./components/base/Errors/NotFound")),
            hideInMenu: true,
          },
        ],
      },
    ].filter(Boolean),
  },
];

export const authorize = (
  permissions: Dictionary<boolean>,
  items?: Array<[Resource, Permission]>,
  isAdmin?: boolean
) => {
  if (isAdmin) return true;
  if (!items) return true;

  return items.some(
    ([resource, permission]) => permissions[`${resource}:${permission}`]
  );
};

export const protectRoutes = (
  routes: IRouteConfig[] | undefined,
  roles: Dictionary<boolean>,
  isAdmin?: boolean,
  userType?: UserType
) => {
  if (!routes) return [];

  const protectedRoute: any[] = [];
  routes.forEach((route) => {
    const { permissions, permissionUser } = route;
    if (!isEmpty(permissions) && !isEmpty(permissionUser) && userType) {
      if (
        isAdmin ||
        (permissions?.some(
          ([resource, permission]: any) => roles[`${resource}:${permission}`]
        ) &&
          permissionUser?.includes(userType))
      ) {
        protectedRoute.push({
          ...route,
          routes: protectRoutes(route.routes, roles, isAdmin, userType),
        });
      }
    } else if (!isEmpty(permissions) || !isEmpty(permissionUser)) {
      if (
        isAdmin ||
        permissions?.some(
          ([resource, permission]: any) => roles[`${resource}:${permission}`]
        )
      ) {
        protectedRoute.push({
          ...route,
          routes: protectRoutes(route.routes, roles, isAdmin, userType),
        });
      } else if (userType && permissionUser?.includes(userType)) {
        protectedRoute.push({
          ...route,
          routes: protectRoutes(route.routes, roles, isAdmin, userType),
        });
      } else {
        protectedRoute.push({
          ...route,
          component: NotAuthorized,
          routes: protectRoutes(route.routes, roles, isAdmin, userType),
        });
      }
    } else {
      protectedRoute.push({
        ...route,
        routes: protectRoutes(route.routes, roles, isAdmin, userType),
      });
    }
  });

  return protectedRoute;
};

const untreeify = (arr: IRouteConfig[] = []): Omit<IRouteConfig, "routes">[] =>
  arr.reduce(
    (acc: Omit<IRouteConfig, "routes">[], item) =>
      isEmpty(item.routes)
        ? [...acc, item]
        : [...acc, item, ...untreeify(item.routes)],
    []
  );

const getRoutesByPath = memoize(() => keyBy(untreeify(getRoutes()), "path"));

export default getRoutes;

export { getRoutesByPath, untreeify };
